'use client'

import { useRouter } from 'next/router'
import { CSSProperties, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { createContainer } from 'unstated-next'

import { useBrowserStoredState } from '@syconium/little-miss-figgy'

import { useNavVisibility } from '../../../lib/hooks/useNavVisibility'
import {
  headerHeightPixels,
  promoBarHeightClosedPixels,
  promoBarHeightDesktopPixels as promoBarHeightDesktopPixelsConst,
  promoBarHeightMobilePixels as promoBarHeightMobilePixelsConst,
} from '../../constants'

const useNavContainer = (): {
  isHeaderPushedUp: boolean
  promoBarHeightDesktop: CSSProperties['height']
  promoBarHeightDesktopPixels: number
  promoBarHeightMobile: CSSProperties['height']
  promoBarHeightMobilePixels: number
  promoBarIsOpen: boolean
  setIsHeaderPushedUp: (x: boolean) => void
  setPromoBarIsOpen: (x: boolean) => void
  setPromoBarIsVisible: Dispatch<SetStateAction<boolean>>
  promoBarIsVisible: boolean
  currentPromoBarAndNavHeightDesktop: number
  currentPromoBarAndNavHeightMobile: number
  setFilterBarIsAttachedToNav: Dispatch<SetStateAction<boolean>>
} => {
  // Manage the visibility of the nav bar
  const { isVisible, forceVisibleState } = useNavVisibility()
  const setIsHeaderPushedUp = useCallback(
    (isPushedUp: boolean) => forceVisibleState(!isPushedUp),
    [forceVisibleState]
  )
  const [promoBarIsVisible, setPromoBarIsVisible] = useState(false)
  const [filterBarIsAttachedToNav, setFilterBarIsAttachedToNav] = useState(false)
  const router = useRouter()
  const isCollectionsPage = useMemo(() => {
    return router.pathname.includes('collections') || router.pathname.includes('search')
  }, [router.pathname])
  const currentHeaderHeightMobile = !isVisible ? 0 : headerHeightPixels
  const currentHeaderHeightDesktop = headerHeightPixels
  const {
    state: { promoBarIsOpen },
    setState: setPromoBarSessionState,
  } = useBrowserStoredState<{ promoBarIsOpen: boolean }>({
    initialState: { promoBarIsOpen: true },
    storage: globalThis.sessionStorage,
    storageKey: 'PromoBar.isOpen',
    // Both the promo bar's presence and the search overlay's height are rendered from
    // `promoBarIsOpen`, so reading storage during the first client render (ssr: false)
    // diverges from the server's `initialState` and causes a hydration mismatch
    ssr: true,
  })
  const setPromoBarIsOpen = useCallback(
    (x: boolean) => setPromoBarSessionState({ promoBarIsOpen: x }),
    [setPromoBarSessionState]
  )

  const promoBarHeightDesktopPixels = useMemo(() => {
    return promoBarIsOpen ? promoBarHeightDesktopPixelsConst : promoBarHeightClosedPixels
  }, [promoBarIsOpen])

  const promoBarHeightMobilePixels = useMemo(() => {
    return promoBarIsOpen ? promoBarHeightMobilePixelsConst : promoBarHeightClosedPixels
  }, [promoBarIsOpen])

  const promoBarHeightDesktop = useMemo(() => {
    return `${promoBarHeightDesktopPixels}px`
  }, [promoBarHeightDesktopPixels])

  const promoBarHeightMobile = useMemo(() => {
    return `${promoBarHeightMobilePixels}px`
  }, [promoBarHeightMobilePixels])

  const currentPromoBarAndNavHeightDesktop =
    currentHeaderHeightDesktop +
    (promoBarIsVisible ? promoBarHeightDesktopPixels : 0) +
    (isCollectionsPage && filterBarIsAttachedToNav ? headerHeightPixels : 0)
  const currentPromoBarAndNavHeightMobile =
    currentHeaderHeightMobile + (promoBarIsVisible ? promoBarHeightMobilePixels : 0)

  return {
    isHeaderPushedUp: !isVisible,
    promoBarHeightDesktop,
    promoBarHeightDesktopPixels,
    promoBarHeightMobile,
    promoBarHeightMobilePixels,
    promoBarIsOpen,
    setIsHeaderPushedUp,
    setPromoBarIsOpen,
    currentPromoBarAndNavHeightDesktop,
    currentPromoBarAndNavHeightMobile,
    setPromoBarIsVisible,
    promoBarIsVisible,
    setFilterBarIsAttachedToNav,
  }
}

export const NavContainer = createContainer(useNavContainer)
